import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './HazardAssessmentAcknowledgment.css';
import { Checkbox, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControlLabel, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import API from './services/api';
import { useAuth } from './AuthContext';
import { getDepartmentDisplayName, normalizeDepartmentForBackend } from './utils/departmentLabels';

const ADMIN_SUB_DEPARTMENTS = ['Communications', 'Planning and Development', 'Finance', 'Legislative Services'];

const normalizeDepartmentValue = (value) => String(value || '').trim().toLowerCase();

const isAdministrationMappedDepartment = (department) =>
  ADMIN_SUB_DEPARTMENTS.includes(department);

const getEmployeeLookupDepartment = (department) => {
  const normalizedDepartment = normalizeDepartmentForBackend(department);

  if (!normalizedDepartment) {
    return '';
  }

  if (normalizedDepartment === 'Utilities') {
    return '';
  }

  return normalizedDepartment;
};

const matchesSelectedDepartment = (assessmentDepartment, selectedDepartment) => {
  const normalizedAssessmentDepartment = normalizeDepartmentValue(assessmentDepartment);
  const normalizedSelectedDepartment = normalizeDepartmentValue(selectedDepartment);

  if (!normalizedAssessmentDepartment || !normalizedSelectedDepartment) {
    return false;
  }

  if (isAdministrationMappedDepartment(selectedDepartment)) {
    return ['administration', 'admin', 'administrative'].includes(normalizedAssessmentDepartment);
  }

  return normalizedAssessmentDepartment === normalizedSelectedDepartment ||
    normalizedAssessmentDepartment.includes(normalizedSelectedDepartment) ||
    normalizedSelectedDepartment.includes(normalizedAssessmentDepartment);
};

const getFormalAssessmentTitle = (assessment) =>
  String(
    assessment?.title ||
    assessment?.TITLE ||
    assessment?.name ||
    assessment?.NAME ||
    ''
  ).trim();

const normalizeJobPositionLabel = (value) => {
  let normalized = String(value || '').trim();

  if (!normalized) {
    return '';
  }

  normalized = normalized
    .replace(/\\"/g, '"')
    .replace(/^\[+|\]+$/g, '')
    .replace(/^"+|"+$/g, '')
    .replace(/^'+|'+$/g, '')
    .trim();

  if (!normalized) {
    return '';
  }

  if (normalized.startsWith('ALL_')) {
    const scope = normalized.slice(4).replace(/_/g, ' ').trim();

    if (!scope) {
      return 'All';
    }

    if (scope.toUpperCase() === 'TOWN') {
      return 'All Town';
    }

    return `All ${getDepartmentDisplayName(scope)}`;
  }

  return normalized.replace(/_/g, ' ');
};

const extractJobPositionLabels = (rawValue) => {
  if (Array.isArray(rawValue)) {
    return rawValue.flatMap(extractJobPositionLabels);
  }

  if (rawValue == null) {
    return [];
  }

  if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim();

    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (parsed !== rawValue) {
        return extractJobPositionLabels(parsed);
      }
    } catch {
      // Fall through to string normalization.
    }
  }

  const normalized = normalizeJobPositionLabel(rawValue);
  return normalized ? [normalized] : [];
};

const formatEmployeeDisplayName = (employee = {}) => {
  const firstName = String(employee?.firstName || '').trim();
  const lastName = String(employee?.lastName || '').trim();

  if (lastName && firstName) {
    return `${lastName}, ${firstName}`;
  }

  return lastName || firstName || String(employee?.email || '').trim();
};

const formatPrequalificationContractorContact = (record = {}) => {
  const contactName = String(record.primaryContactName || '').trim();
  const contactPhone = String(record.primaryContactPhone || '').trim();
  const contactEmail = String(record.primaryContactEmail || '').trim();
  const nameAndPhone = [contactName, contactPhone].filter(Boolean).join(' / ');

  return nameAndPhone || contactEmail || 'Contact not provided';
};

const mapPrequalificationContractorOption = (record = {}) => ({
  id: `prequal-${record.id || record.ID}`,
  name: String(record.companyName || record.COMPANY_NAME || 'Contractor').trim(),
  contact: formatPrequalificationContractorContact(record),
  email: String(record.primaryContactEmail || record.PRIMARY_CONTACT_EMAIL || '').trim(),
  source: 'contractor-prequalification',
  contractorPrequalificationId: record.id || record.ID
});

const CustomMultiSelect = ({
  label,
  options = [],
  selectedValues = [],
  onChange,
  placeholder = 'Select options',
  emptyMessage = 'No options available',
  disabled = false,
  summaryText,
  searchable = false,
  searchPlaceholder = 'Search options',
  searchEmptyMessage = 'No options match your search',
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen]);

  const selectedLabels = options
    .filter((option) => selectedValues.includes(option.value))
    .map((option) => option.label);

  const filteredOptions = useMemo(() => {
    const query = String(searchQuery || '').trim().toLowerCase();

    if (!searchable || !query) {
      return options;
    }

    return options.filter((option) => [option.label, option.secondary, option.value, option.searchText]
      .some((value) => String(value || '').toLowerCase().includes(query)));
  }, [options, searchQuery, searchable]);

  const displayedSummary = typeof summaryText === 'function'
    ? summaryText(selectedValues, options)
    : (summaryText || selectedLabels.join(', '));

  const toggleValue = (value) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((selectedValue) => selectedValue !== value));
      return;
    }

    onChange([...selectedValues, value]);
  };

  return (
    <div className={`hazard-multiselect ${className}`.trim()} ref={containerRef}>
      <label className="hazard-multiselect-label">{label}</label>
      {displayedSummary ? (
        <div className="hazard-multiselect-summary">{displayedSummary}</div>
      ) : null}
      <button
        type="button"
        className="hazard-multiselect-toggle"
        onClick={() => !disabled && setIsOpen((currentValue) => !currentValue)}
        disabled={disabled}
      >
        <span>{selectedValues.length > 0 ? `${selectedValues.length} selected` : placeholder}</span>
        <span className="hazard-multiselect-caret">{isOpen ? '▲' : '▼'}</span>
      </button>
      {isOpen ? (
        <div className="hazard-multiselect-panel">
          {searchable ? (
            <div style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#fff', position: 'sticky', top: 0, zIndex: 1 }}>
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={searchPlaceholder}
                style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px' }}
              />
            </div>
          ) : null}
          {options.length === 0 ? (
            <div className="hazard-multiselect-empty">{emptyMessage}</div>
          ) : filteredOptions.length === 0 ? (
            <div className="hazard-multiselect-empty">{searchEmptyMessage}</div>
          ) : (
            filteredOptions.map((option) => (
              <label key={option.value} className="hazard-multiselect-option">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.value)}
                  onChange={() => toggleValue(option.value)}
                />
                <span className="hazard-multiselect-option-text">
                  <span className="hazard-multiselect-option-primary">{option.label}</span>
                  {option.secondary ? (
                    <span className="hazard-multiselect-option-secondary">{option.secondary}</span>
                  ) : null}
                </span>
              </label>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
};

const HazardAssessmentAcknowledgment = () => {
  const { user } = useAuth();
  const [currentUser, setCurrentUser] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [contractorsOnSite, setContractorsOnSite] = useState([]);
  const [formalAssessments, setFormalAssessments] = useState([]);
  const [selectedFormalAssessments, setSelectedFormalAssessments] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [, setAcknowledgedHazards] = useState({});
  const [prequalificationContractorRecords, setPrequalificationContractorRecords] = useState([]);
  
 // Town of Diamond Valley approved contractors with complete details
  const hardCodedContractors = useMemo(() => [
    { id: 1, name: 'Acapulco Pools', contact: 'Mark Elliott - (519) 830-8201', email: 'mark@acapulcopools.com', insuranceExpiry: 'Nov 01, 2023', licenseNumber: 'No records', licenseExpiry: '' },
    { id: 2, name: 'Ainsworth Inc.', contact: 'Trevor Kaiser - (403) 265-6750', email: 'trevor.kaiser@ainsworth.com', insuranceExpiry: 'Oct 31, 2023', licenseNumber: '20230077', licenseExpiry: '31-Dec-2023' },
    { id: 3, name: 'Alsa Road Construction Ltd.', contact: 'David Mac - 403-243-9313', email: 'davidmac@alsaroad.com', insuranceExpiry: 'Feb 01, 2023', licenseNumber: '20240264', licenseExpiry: '31-Dec-2024' },
    { id: 4, name: 'Alta Vac Solutions Ltd', contact: 'Shaun Madigan - (587) 338-1941', email: 'shaun@altavs.ca', insuranceExpiry: 'May 23, 2023', licenseNumber: '20220275', licenseExpiry: '31-Dec-2022' },
    { id: 5, name: 'Aquatech Diving & Marine Services', contact: 'Steve Berube - (780) 913-6690', email: 'info@aquatechdiving.ca', insuranceExpiry: 'Jun 24, 2024', licenseNumber: 'No records', licenseExpiry: '' },
    { id: 34, name: 'Aquatera Utilities Inc.', contact: 'Leo (Leonel) Tobias - ltobias@aquatera.ca', email: 'ltobias@aquatera.ca', insuranceExpiry: '', licenseNumber: 'No records', licenseExpiry: '' },
    { id: 6, name: 'BB Services Ltd.', contact: 'Ken Barrow - (403) 899-8401', email: 'bbservices@telus.net', insuranceExpiry: 'Jun 13, 2024', licenseNumber: '20240111', licenseExpiry: '31-Dec-2024' },
    { id: 7, name: 'Berc Mechanical Ltd.', contact: 'Berkley Crowe - 403-968-9109', email: 'info@bercmech.com', insuranceExpiry: '', licenseNumber: '20240181', licenseExpiry: '31-Dec-2024' },
    { id: 8, name: 'Big Hill Services Ltd.', contact: '403-932-3598', email: 'info@bighillservices.com', insuranceExpiry: 'Oct 29, 2022', licenseNumber: '2021248', licenseExpiry: '16-Jun-2021' },
    { id: 9, name: 'Bow Mark Paving Ltd.', contact: 'Trevor Forbes - (403) 816-9767', email: 'trevor@bowmark.ca', insuranceExpiry: 'Apr 01, 2024', licenseNumber: '20240303', licenseExpiry: '31-Dec-2024' },
    { id: 10, name: 'Boys & Girls Clubs of the Foothills', contact: 'Shirley Puttock - (403) 933-4066', email: 'exdirect@telus.net', insuranceExpiry: 'Apr 01, 2021', licenseNumber: '20240099', licenseExpiry: '31-Dec-2024' },
    { id: 11, name: 'Carmichael Engineering', contact: 'Chris Brown - 403-255-3322', email: 'cbrown@carmichael-eng.ca', insuranceExpiry: '', licenseNumber: '20230126', licenseExpiry: '31-Dec-2023' },
    { id: 12, name: 'CDM Mechanical Ltd.', contact: 'Mark Spak - (403) 652-1777', email: 'mark@cdmmechanical.com', insuranceExpiry: 'Mar 01, 2025', licenseNumber: '20240068', licenseExpiry: '31-Dec-2024' },
    { id: 13, name: 'Chamco Industries', contact: 'Erin Duddy - (403) 777-0835', email: 'eduddy@chamco.com', insuranceExpiry: 'Jul 01, 2025', licenseNumber: 'No records', licenseExpiry: '' },
    { id: 14, name: 'Chikeez Chow Down', contact: 'Patricia Chikowski - (403) 498-4461', email: 'chikeezchowdown@shaw.ca', insuranceExpiry: 'Jun 21, 2024', licenseNumber: '20240393', licenseExpiry: '31-Dec-2024' },
    { id: 15, name: 'CK Fire and Safety Solutions Inc.', contact: 'Chad Kalynko - (430) 805-4042', email: 'infor@ckfireandsafety.com', insuranceExpiry: 'Sep 12, 2023', licenseNumber: '20230331', licenseExpiry: '15-Jun-2023' },
    { id: 16, name: 'Constant Fire Protection Systems Ltd.', contact: 'Paul Haddow - 403-512-9164', email: 'paul.haddow@cfps.ca', insuranceExpiry: 'Jun 15, 2022', licenseNumber: '20220108', licenseExpiry: '31-Dec-2022' },
    { id: 17, name: 'DAVG Consulting Inc.', contact: 'Doreen von Gradulewski - (403) 874-4107', email: 'safety@diamondvalley.town', insuranceExpiry: 'Jan 17, 2024', licenseNumber: '20240030', licenseExpiry: '31-Dec-2024' },
    { id: 18, name: 'Dexon Canada Air Systems Inc.', contact: 'Will Dumonceaux - 403-605-0917', email: 'sales@dexoncanada.com', insuranceExpiry: 'Apr 14, 2022', licenseNumber: 'No records', licenseExpiry: '' },
    { id: 19, name: 'Everest Construction', contact: 'Kelley Risdon - (403) 370-4506', email: 'info@everestconstruction.ca', insuranceExpiry: 'Jul 31, 2023', licenseNumber: '20230008', licenseExpiry: '31-Dec-2023' },
    { id: 20, name: 'Geolyn Pipe Inspection Services Ltd.', contact: 'Ken Lucas - 403-453-0522', email: 'klucas@geolyn.ca', insuranceExpiry: '', licenseNumber: '2021041', licenseExpiry: '31-Dec-2021' },
    { id: 21, name: 'GLV Travellers Inc.', contact: 'Garrett Vogelaar - (780) 951-0224', email: 'garrett@glvt.ca', insuranceExpiry: 'Apr 25, 2026', licenseNumber: '20240364', licenseExpiry: '31-Dec-2024' },
    { id: 22, name: 'Gran-Lee Electric Ltd', contact: 'Grant - (403) 312-6505', email: 'granlee@granlee.ca', insuranceExpiry: 'Feb 04, 2023', licenseNumber: '2019095', licenseExpiry: '31-Dec-2019' },
    { id: 23, name: 'High Country Vac Services', contact: 'Travis Johnson - (403) 888-3978', email: 'travis@highcountryvac.com', insuranceExpiry: 'Mar 03, 2024', licenseNumber: '20240127', licenseExpiry: '31-Dec-2024' },
    { id: 24, name: 'Insituform Technologies Ltd.', contact: 'Brent Clark - 780-893-9573', email: '', insuranceExpiry: '', licenseNumber: '2021220', licenseExpiry: '31-Dec-2021' },
    { id: 25, name: 'Kidco Construction Ltd.', contact: 'Moe Issa - 403-809-2972', email: 'missa@kidco.ca', insuranceExpiry: '', licenseNumber: '20220193', licenseExpiry: '31-Dec-2022' },
    { id: 26, name: 'Little Chinook', contact: 'Randy Stuart - (403) 540-2025', email: 'lchinook@telus.net', insuranceExpiry: 'Jun 01, 2023', licenseNumber: '20240123', licenseExpiry: '31-Dec-2024' },
    { id: 27, name: 'Lodgepole Horticultural Services Inc.', contact: 'Ali Cannon - (403) 862-5659', email: 'lodgepole@telus.net', insuranceExpiry: 'Oct 01, 2021', licenseNumber: '20220244', licenseExpiry: '31-Dec-2022' },
    { id: 28, name: 'Markr Group', contact: 'Suchit Sawant - (250) 712-3393', email: 'suchit.swant@markrgroup.com', insuranceExpiry: 'May 30, 2024', licenseNumber: 'No records', licenseExpiry: '' },
    { id: 29, name: 'Maverick Electrical Service Inc.', contact: 'Dave Hanrahan - 403-510-8112', email: 'dhanrahan@hotmail.com', insuranceExpiry: 'Mar 13, 2023', licenseNumber: '20240143', licenseExpiry: '31-Dec-2024' },
    { id: 30, name: 'MPE Engineering Ltd. (EnGlobe Corp)', contact: 'Jill Hardy - (403) 219-6319', email: 'jhardy@mpe.ca', insuranceExpiry: 'Aug 06, 2023', licenseNumber: 'No records', licenseExpiry: '' },
    { id: 31, name: 'MTK Construction Ltd.', contact: 'Mitch Kratchmer - (403) 634-1772', email: 'mitch@mtkltd.com', insuranceExpiry: 'Jun 27, 2023', licenseNumber: '2021155', licenseExpiry: '31-Dec-2021' },
    { id: 32, name: 'Norstar Service Solutions', contact: 'Sean Fitzsimmons - (403) 771-7791', email: 'seanfitz@norstarsolutions.com', insuranceExpiry: 'Apr 15, 2023', licenseNumber: 'No records', licenseExpiry: '' },
    { id: 33, name: "O'Leary Excavating Ltd.", contact: 'Joey Santelli - (403) 899-8320', email: 'joey.s@olearyexcavating.com', insuranceExpiry: 'Mar 25, 2024', licenseNumber: '20240240', licenseExpiry: '31-Dec-2024' },
    { id: 46, name: 'Park Enterprises Ltd.', contact: 'contact@parkenterprises.ca - (403) 329-3747', email: 'contact@parkenterprises.ca', insuranceExpiry: '', licenseNumber: 'No records', licenseExpiry: '' },
    { id: 47, name: 'Property Assessor', contact: 'Doug Jensen - (403) 315-1324', email: '', insuranceExpiry: '', licenseNumber: 'No records', licenseExpiry: '' },
    { id: 35, name: 'Ridgeline Electrical Services Ltd', contact: 'Kimberly Goertzen - (403) 998-1429', email: 'kgoertzen@ridgelineelectrical.com', insuranceExpiry: 'Oct 13, 2023', licenseNumber: '20240361', licenseExpiry: '31-Dec-2024' },
    { id: 36, name: 'Russpet Construction Ltd', contact: 'Russell Peterson - (403) 291-4404', email: 'russell@russpet.com', insuranceExpiry: 'Jul 30, 2022', licenseNumber: '20220207', licenseExpiry: '30-Jun-2022' },
    { id: 37, name: 'Sharon Young (1951901 Alberta Ltd.)', contact: 'Sharon Young - (403) 863-5150', email: 'SY1951901AlbertaLtd@shaw.ca', insuranceExpiry: 'Feb 03, 2024', licenseNumber: 'No records', licenseExpiry: 'Professional CRSP & Auditing' },
    { id: 38, name: 'Shawne Excavating & Trucking Ltd.', contact: 'Wes Shaw - (403) 321-3975', email: 'wes@shawneexcavating.com', insuranceExpiry: 'Jul 17, 2025', licenseNumber: '20240093', licenseExpiry: '31-Dec-2024' },
    { id: 39, name: 'Slickforce Mechanical Ltd.', contact: 'Brad Price - (403) 312-8013', email: 'slickforce24@gmail.com', insuranceExpiry: 'Dec 11, 2023', licenseNumber: 'No records', licenseExpiry: '' },
    { id: 40, name: 'Superior Fencing - Lee Brothers', contact: 'Patrick Lee - (403) 617-7360', email: 'superiorfeincing@shaw.ca', insuranceExpiry: 'Apr 22, 2024', licenseNumber: 'No records', licenseExpiry: '' },
    { id: 41, name: 'The Patchmaster Ltd.', contact: 'Danielle Mayhew - (403) 512-7915', email: 'dmayhew@thepatchmaster.ca', insuranceExpiry: 'Nov 25, 2025', licenseNumber: '20240366', licenseExpiry: '16-Oct-2024' },
    { id: 42, name: 'Titan Eavestroughing', contact: 'Kirsten - (403) 922-0258', email: 'titaneavestroughing@outlook.com', insuranceExpiry: 'Sep 21, 2022', licenseNumber: 'No records', licenseExpiry: '' },
    { id: 43, name: 'Trane Canada ULC', contact: 'Brian Erdman - 403-660-8352', email: 'bryan123@telus.net', insuranceExpiry: 'Apr 17, 2022', licenseNumber: '20230113', licenseExpiry: '31-Dec-2023' },
    { id: 44, name: 'True Grit Welding Ltd.', contact: 'Jaycee Zeer - 403-652-6493', email: 'jzeer@telus.net', insuranceExpiry: 'Mar 13, 2023', licenseNumber: '20240262', licenseExpiry: '31-Dec-2024' },
    { id: 45, name: 'Urban Systems Ltd.', contact: 'Meghan Aebig - (403) 291-1193', email: 'maebig@urbansystems.ca', insuranceExpiry: 'Jun 30, 2024', licenseNumber: 'No records', licenseExpiry: 'Professional Engineer' },
  ], []);

  const prequalificationContractors = useMemo(() => prequalificationContractorRecords
    .filter((record) => String(record.status || record.STATUS || '').toUpperCase() !== 'ARCHIVED')
    .filter((record) => String(record.companyName || record.COMPANY_NAME || '').trim())
    .map(mapPrequalificationContractorOption), [prequalificationContractorRecords]);

  const availableContractors = useMemo(() => [
    ...hardCodedContractors,
    ...prequalificationContractors
  ].sort((left, right) => left.name.localeCompare(right.name, undefined, { numeric: true, sensitivity: 'base' })), [hardCodedContractors, prequalificationContractors]);
  
 // PPE options
  const ppeOptions = [
    'Chainsaw Pants, Hard Hat, Hearing Protection and Face Shield',
    'Coveralls - Chemical Resistant',
    'Coveralls - Fire Resistant',
    'Coveralls - High Visible, Regular',
    'Face Shield',
    'Fall Protection Harness, Lanyards and SLR',
    'Gloves - Cut Resistant',
    'Gloves - Leather',
    'Gloves - Nitrile',
    'Gloves - Rubber',
    'Hard Hat',
    'Hearing Protection - Double Protection',
    'Hearing Protection - Ear Muffs',
    'Hearing Protection - Ear Plugs',
    'High Visible Vests/Stripes',
    'Respirator - N95 Masks',
    'Respirator - SCBA',
    'Safety Glasses',
    'Safety Goggles',
    'Steel Toed Boots'
  ];

  // Get saved department from localStorage (if available)
  const savedDepartment = localStorage.getItem('lastDepartment') || '';

  const [formData, setFormData] = useState({
    activeFormalAssessments: [], // Changed from single string to array
    selectedTasks: [],
    jobTitle: '',
    primaryDepartment: savedDepartment, // Initialize with saved department
    secondaryDepartment: '',
    hazardsIdentified: [],
    ppeRequired: [],
    equipmentUsed: [],
    contractorsPresent: [],
    talkingPoints: '',
    supervisorName: '',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    contractorsOnSite: false,
    equipmentMaintenanceRepairRequired: '',
    equipmentMaintenanceRepairNotes: '',
    permitRequired: '',
    permitNumber: '',
    digging: false,
    diggingRequired: '',
    locateMapReference: ''
  });

  // Defensive check to ensure arrays are always arrays
  useEffect(() => {
    setFormData(prevData => ({
      ...prevData,
      selectedTasks: Array.isArray(prevData.selectedTasks) ? prevData.selectedTasks : [],
      hazardsIdentified: Array.isArray(prevData.hazardsIdentified) ? prevData.hazardsIdentified : [],
      equipmentUsed: Array.isArray(prevData.equipmentUsed) ? prevData.equipmentUsed : [],
      contractorsPresent: Array.isArray(prevData.contractorsPresent) ? prevData.contractorsPresent : []
    }));
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadPrequalificationContractors = async () => {
      try {
        const response = await API.get('/api/contractorPrequalification/records');
        if (!cancelled) {
          setPrequalificationContractorRecords(Array.isArray(response.data?.records) ? response.data.records : []);
        }
      } catch (error) {
        if (!cancelled) {
          setPrequalificationContractorRecords([]);
        }
        console.error('Unable to load contractor prequalification records for Pre-Job contractor list:', error);
      }
    };

    loadPrequalificationContractors();
    window.addEventListener('focus', loadPrequalificationContractors);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', loadPrequalificationContractors);
    };
  }, []);

  useEffect(() => {
    const availableContractorIds = new Set(availableContractors.map((contractor) => contractor.id));

    setContractorsOnSite((currentContractors) => {
      const nextContractors = currentContractors.filter((contractorId) => availableContractorIds.has(contractorId));
      const unchanged = nextContractors.length === currentContractors.length &&
        nextContractors.every((contractorId, index) => contractorId === currentContractors[index]);

      return unchanged ? currentContractors : nextContractors;
    });
  }, [availableContractors]);

  useEffect(() => {
    API.get('/api/users/me')
      .then(res => {
        if (res.data && res.data.user) {
          setCurrentUser(res.data.user);
          console.log('Setting primary department to user department:', res.data.user.department);
          
          // Create display name for "Created By" field
          const displayName = res.data.user.firstName && res.data.user.lastName 
            ? `${res.data.user.firstName} ${res.data.user.lastName}` 
            : res.data.user.email || res.data.user.username || '';
          
          // Set primaryDepartment to user's department and supervisorName to user's name/email
          setFormData(prev => ({
            ...prev,
            primaryDepartment: res.data.user.department || prev.primaryDepartment,
            supervisorName: displayName
          }));
        }
      })
      .catch(err => {
        console.error('Failed to fetch current user:', err);
      });
  }, []);

  // Also handle user from Auth context if available
  useEffect(() => {
    if (user && !formData.supervisorName) {
      
      // Create display name for "Created By" field
      const displayName = user.firstName && user.lastName 
        ? `${user.firstName} ${user.lastName}` 
        : user.email || user.username || '';
      
      setFormData(prev => ({
        ...prev,
        supervisorName: displayName,
        primaryDepartment: user.department || prev.primaryDepartment
      }));
    }
  }, [user, formData.supervisorName]);

  useEffect(() => {
    const departmentsToLoad = [formData.primaryDepartment, formData.secondaryDepartment]
      .map((department) => getEmployeeLookupDepartment(department))
      .filter(Boolean);

    if (departmentsToLoad.length === 0) {
      setEmployees([]);
      setAttendance([]);
      return;
    }

    let cancelled = false;

    const loadEmployees = async () => {
      try {
        const responses = await Promise.all(
          [...new Set(departmentsToLoad)].map((department) =>
            API.get(`/api/users?department=${encodeURIComponent(department)}`)
          )
        );

        if (cancelled) {
          return;
        }

        const mergedEmployees = responses.flatMap((response) => response?.data?.users || []);
        const uniqueEmployees = [];
        const seenEmails = new Set();

        mergedEmployees.forEach((employee) => {
          const normalizedEmail = String(employee?.email || '').trim().toLowerCase();

          if (normalizedEmail) {
            if (seenEmails.has(normalizedEmail)) {
              return;
            }

            seenEmails.add(normalizedEmail);
          }

          uniqueEmployees.push(employee);
        });

        const sortedEmployees = uniqueEmployees.sort((left, right) => {
          const leftLastName = String(left?.lastName || '').trim();
          const rightLastName = String(right?.lastName || '').trim();
          const lastNameComparison = leftLastName.localeCompare(rightLastName, undefined, { sensitivity: 'base' });

          if (lastNameComparison !== 0) {
            return lastNameComparison;
          }

          const leftFirstName = String(left?.firstName || '').trim();
          const rightFirstName = String(right?.firstName || '').trim();
          return leftFirstName.localeCompare(rightFirstName, undefined, { sensitivity: 'base' });
        });

        setEmployees(sortedEmployees);
        setAttendance((previousAttendance) =>
          previousAttendance.filter((email) =>
            sortedEmployees.some((employee) => employee?.email === email)
          )
        );
      } catch (err) {
        if (cancelled) {
          return;
        }

        console.error('Error fetching employees:', err);
        setEmployees([]);
        setAttendance([]);
      }
    };

    loadEmployees();

    return () => {
      cancelled = true;
    };
  }, [formData.primaryDepartment, formData.secondaryDepartment]);

  // Fetch all active formal assessments. Department-specific mapping happens later
  // in getFilteredFormalAssessments so primary and secondary selections can be unioned.
  const fetchFormalAssessments = useCallback(async () => {
    try {
      const response = await API.get('/api/formalHazardAssessments');
      
      // Filter for approved/active assessments
      const activeAssessments = Array.isArray(response.data) 
        ? response.data.filter(assessment => {
            const status = assessment.status || assessment.STATUS;
            return status === 'Approved' || status === 'Active';
          })
        : [];

      setFormalAssessments(activeAssessments);
    } catch (error) {
      console.error('Error fetching formal assessments:', error);
      setFormalAssessments([]);
    }
  }, []);

  // Fetch formal assessments when component mounts or when departments change
  useEffect(() => {
    fetchFormalAssessments();
  }, [fetchFormalAssessments]);

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showSecondaryDepartment, setShowSecondaryDepartment] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [sendAdvisorReminder, setSendAdvisorReminder] = useState(false);
  const [safetyGuidelines, setSafetyGuidelines] = useState({
    reviewedHazards: false,
    implementedControls: false,
    properPPE: false,
    emergencyProcedures: false,
    obtainedPermits: false
  });
  // Attachments for hazard assessment (photos, docs)
  const [attachments, setAttachments] = useState([]);

  // Filter formal assessments based on selected departments
  const getFilteredFormalAssessments = useCallback(() => {
    const filteredAssessments = (!formData.primaryDepartment && !formData.secondaryDepartment)
      ? formalAssessments
      : formalAssessments.filter(assessment => {
          const assessmentDept = assessment.department || assessment.DEPARTMENT;
          const matchesPrimary = matchesSelectedDepartment(assessmentDept, formData.primaryDepartment);
          const matchesSecondary = matchesSelectedDepartment(assessmentDept, formData.secondaryDepartment);

          return matchesPrimary || matchesSecondary;
        });

    return [...filteredAssessments].sort((left, right) =>
      getFormalAssessmentTitle(left).localeCompare(getFormalAssessmentTitle(right), undefined, {
        numeric: true,
        sensitivity: 'base'
      })
    );
  }, [formalAssessments, formData.primaryDepartment, formData.secondaryDepartment]);

  // Validate selected formal assessments when departments change
  useEffect(() => {
    if (formData.activeFormalAssessments && formData.activeFormalAssessments.length > 0 && formalAssessments.length > 0) {
      const filteredAssessments = getFilteredFormalAssessments();
      const validSelections = formData.activeFormalAssessments.filter(assessmentId => 
        filteredAssessments.find(assessment => 
          (assessment.id || assessment.ID) === parseInt(assessmentId)
        )
      );
      
      // If some selected assessments are not in the filtered list, update selection
      if (validSelections.length !== formData.activeFormalAssessments.length) {
        setFormData(prevData => ({
          ...prevData,
          activeFormalAssessments: validSelections
        }));
        
        if (validSelections.length === 0) {
          setSelectedFormalAssessments([]);
          setAvailableTasks([]);
          setSelectedTasks([]);
          setAcknowledgedHazards({});
        } else {
          const validAssessments = formalAssessments.filter(a => 
            validSelections.includes(String(a.id || a.ID))
          );
          setSelectedFormalAssessments(validAssessments);
        }
      }
    }
  }, [formData.activeFormalAssessments, formData.primaryDepartment, formData.secondaryDepartment, formalAssessments, getFilteredFormalAssessments]);

  const departments = [
    'Communications',
    'Finance',
    'Legislative Services',
    'Municipal Enforcement',
    'Parks',
    'Planning and Development',
    'Recreation',
    'Roads',
    'Utilities'
  ];

  const utilitiesHazards = [
    'Cleaning Wet Wells (Contracted Services)',
    'Driving Long Distances',
    'Generator Maintenance and Repairs',
    'H2S Data Logger Change Out',
    'PRV - Pressure Release Valve Inspections',
    'Pulling Sewage Pumps',
    'Pumping out water and wastewater vaults',
    'Reading Water Meters',
    'Testing and maintenance of fire hydrants',
    'Transferring Bioxide from tanker truck to TV lift station',
    'Using Generators',
    'Water and Wastewater Inspections & Sampling',
    'Water Distribution Valve Maintenance (Annual Checks)',
    'Water Main Repairs with Contractors',
    'Water Meter Repairs/Replacement',
    'Working on Electrical inside/outside Lift Stations (Contracted Services)'
  ];

  const parksHazards = [
    'Baseball Diamond Drag',
    'Blowing off Sidewalks and Pathways',
    'Brush Cutting and Tree Removal',
    'Building Fence',
    'Campground',
    'Cell Phone Usage',
    'Changing Garbages/Picking up Garbage',
    'Cold Mix Usage for TV outdoor Rink',
    'Cutting Timber for building/Repairing',
    'Cutting Trees, Suckers, Brush on Storm Pond/Creeks',
    'Equipment/Vehicle Maintenance, hand tools and PPE Inspections',
    'Fertilizing',
    'Flooding TV Rink w/Zamboni Attachment',
    'Fuelling Vehicles and Equipment',
    'General Administrative',
    'Holiday and Event Decorating',
    'Hose Flooding',
    'Installing Posts',
    'Mulching',
    'Outdoor Rink Maintenance',
    'Outdoor Rink Snow Removal',
    'Painting of Landscape Feature',
    'Planting',
    'Playground Inspections',
    'Pruning trees/Shrubs including overhead',
    'Pumping out Spray Park Sump',
    'Removing Wasp Nests',
    'Rough Mowing',
    'Snow Removal from Pathways, Entrance, Exits',
    'Sports Field Maintenance',
    'Toro',
    'Vehicular Equipment Operation',
    'Watering Flowers Trees',
    'Weed Eating/Push Mower',
    'Weeding by hand/tool',
    'Working at Heights'
  ];

  const recreationHazards = [
    'Advertising for Local Events/Town Events',
    'Laying Sod',
    'Arena Cleaning incl Mopping/floor machine',
    'Manlift Operation',
    'Backwash at Lander Pool',
    'Morning Checks and Cleaning Worksites',
    'Blade Change',
    'Opening Flood Gates & Overhead Doors',
    'Broken Glass',
    'Outdoor Rink Snow Removal',
    'Building Maintenance',
    'Pad Cleaning',
    'Cell Phone Usage',
    'Painting Inside Arena & SSSR Building',
    'Changing and Changing Boards for Advertising',
    'Placing Nets on Ice',
    'Changing Garbage/Picking up Garbage',
    'Plant & Compressor Checks',
    'Cleaning Public Toilets/Showers',
    'Pool Acid Barrel Refill at Dr. Lander Pool',
    'Cold Mix Usage for TV outdoor Rink',
    'Pumping out Spray Park Sump',
    'Acid Barrel Refill at Spray Park and Dr. Lander pool',
    'Recreation Snow Removal Pathway Plowing',
    'Dr. Lander Pool Transferring Chlorine Barrels from Storage Site Windsor Garage to Dr. Lander Pool',
    'Removing Wasp Nests',
    'Equipment/Vehicle Maintenance and Checks',
    'Setting Up Tents',
    'Flooding TV Rink w/Zamboni Attachment',
    'Snow Removal: Pathway Plowing, Removal, & Sanding',
    'Fuelling Vehicles and Equipment',
    'Spray Park Mechanical Room Inspections',
    'General Administrative',
    'SSSR and ODR Maintenance',
    'Hose Flooding Outdoor Rinks',
    'Using Generators',
    'Ice Maintenance',
    'Vehicular Equipment Operation',
    'Ice Making',
    'Working at Heights',
    'Intoxicated/verbally/physically Abusive Patron',
    'Working on Greasy Floors',
    'Laying Rubber Flooring'
  ];

  const roadHazards = [
    'Brush Cutting and Tree Removal',
    'Cell Phone Usage',
    'Equipment/Vehicle Maintenance, hand tools and PPE Inspections',
    'Flushing Sewer Lines',
    'Fueling Vehicles and Equipment',
    'General Office/Shop Duties, Laborer, Working in Traffic',
    'Grading and Street Cleaning',
    'Ground Disturbance - Watermain breaks, Planting posts',
    'Internal Emergencies Roads & Utilities Shop/Work Sites/Westend',
    'Maintenance Inspections',
    'Snow Removal: Plowing, Removal, & Sanding',
    'Storm Drain Maintenance',
    'Vehicle and Mobile Equipment Operation',
    'Welding - Small Jobs',
    'Working at Heights Using Ladders'
  ];

  const parksEquipment = [
    'Bobcat 72" mower (TV0039)',
    'Bobcat SB200 72" snow blower (TV0040)',
    'Chevrolet Silverado 1500 (PT009)',
    'Chevrolet Silverado 1500 (PT010)',
    'Club Car CarryAll 295 (PE003)',
    'FC200 Flail Mower (TV0037)',
    'Ford 350 2011 (PT002)',
    'Ford F150 2016 (PT008)',
    'Ford F350 2016 (PT011)',
    'Frontier (PFP-Unit 4)',
    'Gas Powered Water Pump',
    'Husqvarna Push Mower BD1',
    'Husqvarna Push Mower BD2',
    'Husqvarna Weed Eater BD1',
    'Husqvarna Weed Eater BD2',
    'Husqvarna Weed Eater BD3',
    'Husqvarna Weed Eater BD4',
    'Husqvarna Weed Eater BD5',
    'Husqvarna Weed Eater BD6',
    'John Deere 68" angle broom (TV0038)',
    'Kubota Rough Mower RCR1872 (TV1)',
    'Kubota RTV-X1100C (PE001)',
    'Kubota Tractor L3560 L3560HSTCC (PE002)',
    'Kubota Zero Turn ZD12110 (PE008)',
    'Mikita Leaf Blower BD 2',
    'Mikita Leaf Blower BD 3',
    'SE03 Black Landscape Trailer (5CJ 561)',
    'Stihl Leaf Blower BD 1',
    'Toolcat 68 Angle Broom (TV1)',
    'Toolcat 72 Snow Blade (TV1)',
    'Toolcat Snow Blower SB200 x 72 (TV1)',
    'Toro Groundmaster 400D (PE004)',
    'Toro Groundmaster 401D (PE006)',
    'Toro Groundsmaster 4000D (PE005)',
    'Willie 265S (PE007)',
    'Zamboni 200 (TV0044)'
  ];

  const recreationEquipment = [
    'Bobcat 5600 ToolCat (RECE003)',
    'Olympia Millenium (RECE001)',
    'Olympia Millennium (RECE002)',
    'Chevrolet Silverado 2024 (REC003)',
    'Chevrolet Silverado 2024 (REC004)'
  ];

  const administrationEquipment = [
    'Fire Warden Safety Backpack',
    'Planning Safety Backpack'
  ];

  const roadEquipment = [
    'John Deer Skid Steer (RE2025)',
    'GMC Sierra 1500 (RT002)',
    'Freightliner 2004 (RT010)',
    'International 2008 (RT013)',
    '6" Water Pump (unit 19)',
    'Case 5805SN Backhoe (RE013)',
    'Clark C25L Forklift (TV0013)',
    'Dams w/container (fillable with water, sand, etc)',
    'Dynapac 5.5HP Packer (TV0029)',
    'Frontier Atlas Copco Generator on Trailer (unit 4)',
    'Gehl Skidsteer (Unit 16)',
    'Genie AWP-30 (TV0023)',
    'Genset SDMO Generator backup (unit 5)',
    'Global Pump 6" Trash Pump (6GSTAPJ4TSALF) mounted on Trailer (RE012)',
    'Grizzly Skid mount Hotsy (TV0058)',
    'H&H Tilt Deck Trailer (unit 47)',
    'H&H Tr & Mounted US10 SDMA Generator Red Sign Trailer',
    'Homemade 16\' Shoring Trailer (white) (unit 46)',
    'Homemade 20\' trailer (P02064)',
    'Hotsy Steamer (unit 43)',
    'Innovative 13 WF-CH 16\' Trailer (unit 34)',
    'International 7000 Tandem (RT009)',
    'John Deere 624K Loader (RE005)',
    'John Deere 772G Grader (RE003)',
    'John Deere Grader 770D (RE004)',
    'John Deere Loader 624K (RE006)',
    'Multi Bagger Sandbagger & Conveyor (unit 3)',
    'Peterbilt 348 T/A Dump Truck (RT008)',
    'RPM217 Snow Blower/Loader (TV0033)',
    'Wille 855 (RE008)',
    'Winco Light Tower WQ35LT/B w/generator (RE010)',
    'Ford F350 2016 (PT011)',
    'GMC 1 Ton (RT004)',
    'Chevrolet 1/2 ton 2024 (RT014)',
    'Chevrolet 1/2 ton 2024 (RT015)',
    'Chevrolet 1/2 ton 2024 (RT016)',
    'Chevrolet 1/2 ton 2024 (UT005)',
    'Chevrolet 1/2 ton 2024 (UT004)',
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'primaryDepartment' && value) {
      localStorage.setItem('lastDepartment', value);
    }

    if (type === 'checkbox') {
      setFormData(prevData => {
        return {
          ...prevData,
          [name]: checked
        };
      });
    } else {
      setFormData(prevData => {
        const updatedData = {
          ...prevData,
          [name]: value
        };

        if (name === 'equipmentMaintenanceRepairRequired' && value !== 'Yes') {
          updatedData.equipmentMaintenanceRepairNotes = '';
        }
        
        // Clear formal assessment selection when departments change
        if (name === 'primaryDepartment' || name === 'secondaryDepartment') {
          updatedData.activeFormalAssessments = [];
          // Also clear related states
          setSelectedFormalAssessments([]);
          setAvailableTasks([]);
          setSelectedTasks([]);
          setAcknowledgedHazards({});
        }
        
        return updatedData;
      });

      if (name === 'secondaryDepartment' && value === '') {
        setFormData(prevFormData => {
          return {
            ...prevFormData,
            hazardsIdentified: Array.isArray(prevFormData.hazardsIdentified) 
              ? prevFormData.hazardsIdentified.filter(hazard => !getHazards().includes(hazard))
              : []
          };
        });
      }
    }
  };

  // Helper function to group tasks with the same name for selection
  const groupTasksForSelection = (tasks) => {
    const taskGroups = new Map();
    
    tasks.forEach((task, originalIndex) => {
      const taskName = task.task || `Task ${originalIndex + 1}`;
      
      if (taskGroups.has(taskName)) {
        // Add this instance to the existing group
        taskGroups.get(taskName).instances.push({
          ...task,
          originalIndex: originalIndex
        });
      } else {
        // Create new group with this instance
        taskGroups.set(taskName, {
          taskName: taskName,
          instances: [{
            ...task,
            originalIndex: originalIndex
          }],
          // Use the first instance for display info
          description: task.description,
          displayIndex: originalIndex
        });
      }
    });
    
    // Convert to array for rendering
    return Array.from(taskGroups.values());
  };

  // Handle formal assessment dropdown selection (Material-UI Select component)
  const handleFormalAssessmentDropdownChange = (selectedValues) => {
    // selectedValues is an array of selected assessment IDs
    const updatedAssessmentIds = selectedValues;
    
    // If no assessments are selected, clear everything
    if (updatedAssessmentIds.length === 0) {
      setSelectedFormalAssessments([]);
      setFormData(prevData => ({
        ...prevData,
        activeFormalAssessments: [],
        jobTitle: '', // Clear job title when no assessments are selected
        selectedTasks: []
      }));
      setAvailableTasks([]);
      setSelectedTasks([]);
      return;
    }
    
    // Get selected assessments
    const selectedAssessments = formalAssessments.filter(a => 
      updatedAssessmentIds.includes(String(a.id || a.ID))
    );
    
    setSelectedFormalAssessments(selectedAssessments);
    
    // Combine job positions from all selected assessments
    let combinedJobTitles = [];
    selectedAssessments.forEach(assessment => {
      combinedJobTitles.push(
        ...extractJobPositionLabels(assessment?.jobPositions ?? assessment?.JOB_POSITIONS)
      );
    });
    
    // Remove duplicates and join
    const uniqueJobTitles = [...new Set(combinedJobTitles)];
    const jobTitleValue = uniqueJobTitles.join(', ');
    
    setFormData(prevData => ({
      ...prevData,
      activeFormalAssessments: updatedAssessmentIds,
      jobTitle: jobTitleValue, // Automatically populate job title from formal assessments
      selectedTasks: [] // Clear selected tasks when assessments change
    }));

    // Combine available tasks from all selected assessments
    let allTasks = [];
    selectedAssessments.forEach(assessment => {
      if (assessment && assessment.tasks) {
        
        let tasks = [];
        if (Array.isArray(assessment.tasks)) {
          tasks = assessment.tasks;
        } else if (typeof assessment.tasks === 'string') {
          try {
            tasks = JSON.parse(assessment.tasks);
          } catch (e) {
            console.error('Failed to parse tasks JSON:', e);
            tasks = [];
          }
        }
        
        allTasks.push(...tasks);
      }
    });
    
    // Group tasks with the same name for selection
    const groupedTasks = groupTasksForSelection(allTasks);
    
    setAvailableTasks(groupedTasks);
    setSelectedTasks([]);
  };

  // Handle task selection - when a task group is selected, add all instances
  const handleTaskSelection = (taskIndex) => {
    const taskGroup = availableTasks[taskIndex];
    if (!taskGroup) return;

    // Check if any instance of this task group is already selected
    const hasSelectedInstances = taskGroup.instances.some(instance => 
      selectedTasks.some(selected => selected.originalIndex === instance.originalIndex)
    );

    if (hasSelectedInstances) {
      // Remove all instances of this task group
      const updatedSelectedTasks = selectedTasks.filter(selected => 
        !taskGroup.instances.some(instance => selected.originalIndex === instance.originalIndex)
      );
      setSelectedTasks(updatedSelectedTasks);
      setFormData(prevData => ({
        ...prevData,
        selectedTasks: updatedSelectedTasks
      }));
      
      // Clear acknowledged hazards for all removed instances
      setAcknowledgedHazards(prevAcknowledged => {
        const newAcknowledged = { ...prevAcknowledged };
        taskGroup.instances.forEach(instance => {
          delete newAcknowledged[instance.originalIndex];
        });
        return newAcknowledged;
      });
    } else {
      // Add all instances of this task group
      const newSelectedTasks = [...selectedTasks];
      taskGroup.instances.forEach(instance => {
        newSelectedTasks.push({
          ...instance,
          index: instance.originalIndex, // Use original index for compatibility
          groupName: taskGroup.taskName,
          instanceCount: taskGroup.instances.length
        });
      });
      
      setSelectedTasks(newSelectedTasks);
      setFormData(prevData => ({
        ...prevData,
        selectedTasks: newSelectedTasks
      }));
    }
  };

  // Remove task from selection
  const removeTaskFromSelection = (taskIndex) => {
    const updatedTasks = selectedTasks.filter(t => t.index !== taskIndex);
    setSelectedTasks(updatedTasks);
    setFormData(prevData => ({
      ...prevData,
      selectedTasks: updatedTasks
    }));
    
    // Clear acknowledged hazards for removed task
    setAcknowledgedHazards(prevAcknowledged => {
      const updated = { ...prevAcknowledged };
      delete updated[taskIndex];
      return updated;
    });
  };

  // Parse hazards from task hazards string - handles different formats
  const parseTaskHazards = (hazardsString) => {
    if (!hazardsString || typeof hazardsString !== 'string') return [];
    
    // Split by common delimiters and clean up
    const hazards = hazardsString
      .split(/[;,\n\r]|(?:\d+\.)|(?:•)|(?:-\s)/)
      .map(h => h.trim())
      .filter(h => h.length > 3) // Filter out very short entries
      .filter(h => !h.match(/^[0-9]+\.?$/)) // Filter out standalone numbers
      .slice(0, 10); // Limit to reasonable number
    
    return hazards;
  };

  const handlePPESelection = (selectedPPE) => {
    setFormData(prevData => ({
      ...prevData,
      ppeRequired: selectedPPE,
    }));
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    const newAttachments = files.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      uploaded: false
    }));
    setAttachments(prev => [...prev, ...newAttachments]);
  };

  const removeAttachment = (id) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const uploadAttachmentsToServer = async (hazardAssessmentId) => {
    if (!attachments || attachments.length === 0) return { uploaded: 0, failed: 0 };

    const results = await Promise.all(attachments.map(async (attachment) => {
      if (attachment.uploaded) return { attachment, skipped: true };

      const fd = new FormData();
      fd.append('file', attachment.file);
      fd.append('hazardAssessmentId', hazardAssessmentId);

      try {
        console.log(`📎 Uploading attachment ${attachment.name} for hazardAssessment ${hazardAssessmentId}`);
  // Let axios/browser set the Content-Type for FormData so the correct
  // multipart boundary is included; do not set Content-Type manually.
  const res = await API.post('/api/hazards/attachments', fd);
        const data = res && res.data ? res.data : null;
        console.log('📥 Attachment upload response data:', data);

        const updated = {
          ...attachment,
          uploaded: !!(data && (data.success || res.status === 200)),
          serverId: data?.fileId || data?.id || (data?.attachment && data.attachment.id) || null,
          downloadUrl: data?.downloadUrl || null,
          linkedToAssessment: !!data?.linkedToAssessment
        };

        // Update attachments state immutably
        setAttachments(prev => prev.map(a => a.id === attachment.id ? updated : a));

        return { attachment: updated, error: null };
      } catch (err) {
        const errPayload = err && err.response ? err.response.data || err.response : err;
        console.error('❌ Failed to upload attachment', attachment.name, errPayload);
        // mark as failed in UI state
        const failedUpdated = { ...attachment, uploaded: false, uploadError: errPayload };
        setAttachments(prev => prev.map(a => a.id === attachment.id ? failedUpdated : a));
        return { attachment: failedUpdated, error: errPayload };
      }
    }));

    const uploadedCount = results.filter(r => r.attachment && r.attachment.uploaded).length;
    const failedCount = results.filter(r => r.error).length;
    console.log(`✅ Attachment upload process complete — uploaded: ${uploadedCount}, failed: ${failedCount}`);
    return { uploaded: uploadedCount, failed: failedCount };
  };

  const handleEquipmentSelection = (event) => {
    const value = event.target.value;

    setFormData(prevData => ({
      ...prevData,
      equipmentUsed: value,
    }));
  };

  const getHazards = () => {
    let hazards = [];
    
    // Add traditional department-specific hazards
    if (formData.primaryDepartment === 'Utilities') {
      hazards = hazards.concat(utilitiesHazards);
    } else if (formData.primaryDepartment === 'Parks') {
      hazards = hazards.concat(parksHazards);
    } else if (formData.primaryDepartment === 'Recreation') {
      hazards = hazards.concat(recreationHazards);
    } else if (formData.primaryDepartment === 'Roads') {
      hazards = hazards.concat(roadHazards);
    }

    if (formData.secondaryDepartment === 'Utilities') {
      hazards = hazards.concat(utilitiesHazards);
    } else if (formData.secondaryDepartment === 'Parks') {
      hazards = hazards.concat(parksHazards);
    } else if (formData.secondaryDepartment === 'Recreation') {
      hazards = hazards.concat(recreationHazards);
    } else if (formData.secondaryDepartment === 'Roads') {
      hazards = hazards.concat(roadHazards);
    }

    // Add formal assessments for the selected departments
    
    const relevantFormalAssessments = formalAssessments.filter(assessment => {
      const dept = assessment.department || assessment.DEPARTMENT;

      return matchesSelectedDepartment(dept, formData.primaryDepartment) ||
        matchesSelectedDepartment(dept, formData.secondaryDepartment);
    });

    // Add formal assessment titles as hazard options
    const formalAssessmentHazards = relevantFormalAssessments.map(assessment => {
      const title = assessment.title || assessment.TITLE || 'Untitled Assessment';
      return `[Formal Assessment] ${title}`;
    });

    hazards = hazards.concat(formalAssessmentHazards);
    
    return hazards;
  };

  const getEquipment = () => {
    let equipment = [];

    if (['Finance', 'Legislative Services', 'Planning and Development'].includes(formData.primaryDepartment)) {
      equipment = equipment.concat(administrationEquipment);
    } else if (formData.primaryDepartment === 'Parks') {
      equipment = equipment.concat(parksEquipment);
    } else if (formData.primaryDepartment === 'Recreation') {
      equipment = equipment.concat(recreationEquipment);
    } else if (formData.primaryDepartment === 'Roads') {
      equipment = equipment.concat(roadEquipment);
    }


    if (['Finance', 'Legislative Services', 'Planning and Development'].includes(formData.secondaryDepartment)) {
      equipment = equipment.concat(administrationEquipment);
    } else if (formData.secondaryDepartment === 'Parks') {
      equipment = equipment.concat(parksEquipment);
    } else if (formData.secondaryDepartment === 'Recreation') {
      equipment = equipment.concat(recreationEquipment);
    } else if (formData.secondaryDepartment === 'Roads') {
      equipment = equipment.concat(roadEquipment);
    }

    return Array.from(new Set(equipment)).sort((left, right) => left.localeCompare(right));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.equipmentMaintenanceRepairRequired) {
      alert('Please indicate whether any selected equipment or assets require maintenance or repair.');
      return;
    }

    if (
      formData.equipmentMaintenanceRepairRequired === 'Yes' &&
      !String(formData.equipmentMaintenanceRepairNotes || '').trim()
    ) {
      alert('Please enter the maintenance or repair details for the selected equipment or assets.');
      return;
    }

    setOpenDialog(true);
  };

  const handleDialogClose = async (acknowledged = false) => {
    setOpenDialog(false);

    if (acknowledged) {
      try {
        // Map selected formal assessments to hazards_identified format
        const hazardsFromFormalAssessments = selectedFormalAssessments.map((assessment, index) => {
          
          // Try multiple possible title properties
          let title = assessment.title || assessment.TITLE || assessment.name || assessment.NAME || 
                     assessment.assessmentTitle || assessment.ASSESSMENT_TITLE ||
                     assessment.assessmentName || assessment.ASSESSMENT_NAME;
          
          // If title is still an object, try to extract string value
          if (typeof title === 'object' && title !== null) {
            title = title.value || title.text || title.name || title.title || JSON.stringify(title);
          }
          
          // If still no valid title, use the assessment ID
          if (!title || typeof title !== 'string') {
            title = `Assessment ${assessment.id || assessment.ID || index + 1}`;
          }
          
          return `[Formal Assessment] ${title}`;
        });

        // Map contractor IDs to contractor names
        const contractorNames = contractorsOnSite.map(contractorId => {
          const contractor = availableContractors.find(c => c.id === contractorId);
          return contractor ? contractor.name : contractorId;
        });

        const hazardAssessmentData = {
          jobTitle: String(formData.jobTitle || '').substring(0, 250),
          primaryDepartment: String(formData.primaryDepartment || '').substring(0, 50),
          secondaryDepartment: formData.secondaryDepartment ? String(formData.secondaryDepartment).substring(0, 50) : null,
          hazardsIdentified: hazardsFromFormalAssessments, // Map formal assessments to hazards_identified
          ppeRequired: formData.ppeRequired,
          equipmentUsed: formData.equipmentUsed,
          equipmentMaintenanceRepairRequired: formData.equipmentMaintenanceRepairRequired,
          equipmentMaintenanceRepairNotes: formData.equipmentMaintenanceRepairRequired === 'Yes'
            ? String(formData.equipmentMaintenanceRepairNotes || '').substring(0, 2000)
            : null,
          talkingPoints: formData.talkingPoints ? formData.talkingPoints.substring(0, 2000) : null,
          supervisorName: String(formData.supervisorName || '').substring(0, 250),
          date: formData.date || '',
          time: formData.time || '',
          contractorsOnSite: contractorNames, // Use the mapped contractor names
          contractorsPresent: contractorsOnSite.length > 0, // Boolean - true if any contractors selected
          // Send contractor data in multiple formats to ensure backend compatibility
          contractors: contractorNames, // Alternative field name
          contractorsList: contractorNames, // Another alternative
          contractorIds: contractorsOnSite, // Original IDs in case needed
          permitRequired: formData.permitRequired,
          permitNumber: formData.permitRequired === 'Yes' ? formData.permitNumber : null,
          digging: formData.digging,
          diggingRequired: formData.diggingRequired,
          locateMapReference: formData.diggingRequired === 'Yes' ? formData.locateMapReference : null,
          safetyGuidelines: {
            reviewedHazards: safetyGuidelines?.reviewedHazards || false,
            implementedControls: safetyGuidelines?.implementedControls || false,
            properPPE: safetyGuidelines?.properPPE || false,
            emergencyProcedures: safetyGuidelines?.emergencyProcedures || false,
            obtainedPermits: safetyGuidelines?.obtainedPermits || false,
          },
          user: currentUser ? currentUser.username : null,
          attendance,
        };

        const createRes = await API.post('/api/hazards/acknowledgments', hazardAssessmentData);
        const assessmentId = createRes?.data?.assessmentId || null;
  // expose last created id for diagnostic button
  try { window.__lastCreatedHazardAssessmentId = assessmentId; } catch (e) { /* ignore */ }

        if (sendAdvisorReminder && assessmentId) {
          try {
            await API.post('/api/hazards/advisor-reminder', {
              assessmentId,
              requestedBy: currentUser?.email || user?.email || user?.username || null
            });
          } catch (advisorReminderError) {
            console.error('Failed to send H&S advisor reminder email:', advisorReminderError);
          }
        }

        // Debug: show assessment ID and attendance at submit time so we can trace missing POSTs
        try {
          console.log('Hazard assessment created, assessmentId=', assessmentId, 'attendance=', attendance);
        } catch (logErr) {
          // ignore logging errors
        }

        // Upload attachments (non-blocking for invitation flow)
        try {
          if (assessmentId && attachments && attachments.length > 0) {
            // attempt upload and log summary; do not block invitations
            uploadAttachmentsToServer(assessmentId).then(summary => {
              console.log('Attachment upload summary:', summary);
            }).catch(err => {
              console.error('Attachment upload encountered errors:', err);
            });
          }
        } catch (attErr) {
          console.error('Error starting attachment upload:', attErr);
        }

        // Create/send acknowledgment invitations for each employee in attendance.
        // Uses server route /api/hazards/acknowledgment-invitations which integrates
        // with the existing notifications system to send the link.
        try {
          const emails = Array.isArray(attendance) ? attendance.filter(Boolean) : [];
          console.log('Preparing to send invitations, filtered emails=', emails);

          if (emails.length === 0) {
            console.warn('No attendance emails selected — skipping invitation POST.');
          } else {
            const invRes = await API.post('/api/hazards/acknowledgment-invitations', {
              hazardAssessmentId: assessmentId || null,
              incidentId: null,
              emails,
              createdBy: currentUser?.email || user?.email || user?.username || null
            });
            console.log('Acknowledgment invitations response:', invRes?.data);
          }
        } catch (inviteErr) {
          console.error('Failed to create/send acknowledgment invitations:', inviteErr);
          // non-blocking: still mark the form submitted for the creator
        }

        setFormSubmitted(true);
      } catch (error) {
        console.error('Error submitting hazard assessment:', error);
        const serverError = error?.response?.data?.error;
        const missingFields = error?.response?.data?.missingFields;
        const detail = Array.isArray(missingFields) && missingFields.length > 0
          ? ` Missing: ${missingFields.join(', ')}.`
          : (serverError ? ` ${serverError}` : '');
        alert(`An error occurred while submitting the hazard assessment.${detail}`);
      }
    }
  };

  const handleSafetyGuidelineChange = (e) => {
    const { name, checked } = e.target;
    setSafetyGuidelines({
      ...safetyGuidelines,
      [name]: checked
    });
  };

  const allSafetyGuidelinesChecked = Object.values(safetyGuidelines).every(Boolean);

  const resetForm = () => {
    const resetNow = new Date();
    const savedDept = localStorage.getItem('lastDepartment') || '';

    setFormData({
      activeFormalAssessments: [],
      selectedTasks: [],
      jobTitle: '',
      primaryDepartment: savedDept,
      secondaryDepartment: '',
      hazardsIdentified: [],
      ppeRequired: [],
      equipmentUsed: [],
      contractorsPresent: [],
      supervisorName: '',
      date: resetNow.toISOString().split('T')[0],
      time: resetNow.toTimeString().slice(0, 5),
      contractorsOnSite: false,
      equipmentMaintenanceRepairRequired: '',
      equipmentMaintenanceRepairNotes: '',
      permitRequired: '',
      permitNumber: '',
      digging: false,
      diggingRequired: '',
      locateMapReference: '',
      talkingPoints: ''
    });
    setAttendance([]);
    setContractorsOnSite([]);
    setSelectedFormalAssessments([]);
    setSelectedTasks([]);
    setAvailableTasks([]);
    setFormSubmitted(false);
    setShowSecondaryDepartment(false);
    setSendAdvisorReminder(false);
  };

  if (formSubmitted) {
    return (
      <div className="success-container">
        <h2 className="success-header">Assessment Acknowledged</h2>
        <p className="success-message">Thank you for completing the pre-job hazard assessment acknowledgment.</p>
  <div className="summary-container">
          <h3 className="summary-header">Summary:</h3>
          {formData.activeFormalAssessments && formData.activeFormalAssessments.length > 0 && selectedFormalAssessments && selectedFormalAssessments.length > 0 && (
            <p className="summary-row">
              <span className="summary-label">Formal Assessments: </span>
              {selectedFormalAssessments.map(assessment => assessment.title || assessment.TITLE).join(', ')}
            </p>
          )}
          {selectedTasks.length > 0 && (
            <p className="summary-row">
              <span className="summary-label">Selected Tasks: </span>
              {selectedTasks.map(task => task.task || `Task ${task.index + 1}`).join(', ')}
            </p>
          )}
          <p className="summary-row"><span className="summary-label">Job: </span>{formData.jobTitle}</p>
          <p className="summary-row"><span className="summary-label">Primary Department: </span>{getDepartmentDisplayName(formData.primaryDepartment)}</p>
          {formData.secondaryDepartment && (
            <p className="summary-row"><span className="summary-label">Secondary Department: </span>{getDepartmentDisplayName(formData.secondaryDepartment)}</p>
          )}
          <p className="summary-row"><span className="summary-label">PPE Required: </span>{formData.ppeRequired.join(', ')}</p>
          <p className="summary-row"><span className="summary-label">Equipment Used: </span>{formData.equipmentUsed.join(', ')}</p>
          <p className="summary-row"><span className="summary-label">Equipment / Asset Maintenance or Repair Required: </span>{formData.equipmentMaintenanceRepairRequired}</p>
          {formData.equipmentMaintenanceRepairRequired === 'Yes' && formData.equipmentMaintenanceRepairNotes && (
            <p className="summary-row"><span className="summary-label">Maintenance / Repair Details: </span>{formData.equipmentMaintenanceRepairNotes}</p>
          )}
          <p className="summary-row"><span className="summary-label">Supervisor: </span>{formData.supervisorName}</p>
          <p className="summary-row"><span className="summary-label">Date & Time: </span>{formData.date} at {formData.time}</p>
          <p className="summary-row"><span className="summary-label">Attendance: </span>{attendance.map(email => {
            const emp = employees.find(e => e.email === email);
            return emp ? formatEmployeeDisplayName(emp) : email;
          }).join(', ')}</p>
          <p className="summary-row"><span className="summary-label">Contractors on Site: </span>{contractorsOnSite.map(contractorId => {
            const contractor = availableContractors.find(c => c.id === contractorId);
            return contractor ? `${contractor.name} (${contractor.contact})` : contractorId;
          }).join(', ')}</p>
          <p className="summary-row"><span className="summary-label">Permit Required: </span>{formData.permitRequired}</p>
          {formData.permitRequired === 'Yes' && formData.permitNumber && (
            <p className="summary-row"><span className="summary-label">Permit Number: </span>{formData.permitNumber}</p>
          )}
          <p className="summary-row"><span className="summary-label">Digging: </span>{formData.diggingRequired}</p>
          {formData.diggingRequired === 'Yes' && formData.locateMapReference && (
            <p className="summary-row"><span className="summary-label">Locate Map Reference: </span>{formData.locateMapReference}</p>
          )}
        </div>
        <button
          className="reset-button"
          onClick={resetForm}
        >
          Start New Assessment
        </button>
      </div>
    );
  }

  return (
    <div className="container">
      <h2 className="header">Formal Hazard Assessment / Pre-Job Hazard Review</h2>
      <form onSubmit={handleSubmit}>
        {/* Primary Department Selection */}
        <div className="form-group">
          <label className="label" htmlFor="primaryDepartment">
            Primary Department
          </label>
          <select
            className="select"
            id="primaryDepartment"
            name="primaryDepartment"
            value={formData.primaryDepartment}
            onChange={handleInputChange}
          >
            <option value="">Select a department</option>
            {departments.map((department) => (
              <option key={department} value={department}>
                {getDepartmentDisplayName(department)}
              </option>
            ))}
          </select>
        </div>

        {/* Secondary Department Selection */}
        {showSecondaryDepartment && (
          <div className="form-group">
            <label className="label" htmlFor="secondaryDepartment">
              Secondary Department
            </label>
            <select
              className="select"
              id="secondaryDepartment"
              name="secondaryDepartment"
              value={formData.secondaryDepartment}
              onChange={(e) => {
                handleInputChange(e);
                if (e.target.value !== '') {
                  setFormData(prevFormData => ({
                    ...prevFormData,
                    hazardsIdentified: []
                  }));
                }
              }}
            >
              <option value="">Select a department</option>
              {departments.map((department) => (
                <option key={department} value={department}>
                  {getDepartmentDisplayName(department)}
                </option>
              ))}
            </select>
            <IconButton
              onClick={() => {
                setShowSecondaryDepartment(false);
                setFormData(prevFormData => ({
                  ...prevFormData,
                  secondaryDepartment: '',
                  activeFormalAssessments: [], // Clear formal assessments when removing secondary department
                  jobTitle: '', // Clear job title when clearing formal assessments
                  hazardsIdentified: []
                }));
                // Clear related states
                setSelectedFormalAssessments([]);
                setAvailableTasks([]);
                setSelectedTasks([]);
                setAcknowledgedHazards({});
              }}
              aria-label="remove secondary department"
            >
              <RemoveIcon />
            </IconButton>
          </div>
        )}

        {!showSecondaryDepartment && (
          <IconButton
            onClick={() => setShowSecondaryDepartment(true)}
            aria-label="add secondary department"
          >
            <AddIcon />
          </IconButton>
        )}

        {/* Active Formal Assessments Selection */}
        <div className="form-group">
          <CustomMultiSelect
            label="Active Formal Hazard Assessments (Select all that apply)"
            options={getFilteredFormalAssessments().map((assessment) => ({
              value: String(assessment.id || assessment.ID),
              label: assessment.title || assessment.TITLE || 'Untitled Assessment',
              secondary: assessment.department || assessment.DEPARTMENT || ''
            }))}
            selectedValues={formData.activeFormalAssessments}
            onChange={handleFormalAssessmentDropdownChange}
            placeholder="Select formal hazard assessments"
            emptyMessage={`No formal assessments available for: ${[formData.primaryDepartment, formData.secondaryDepartment].filter(Boolean).map(getDepartmentDisplayName).join(', ')}`}
            disabled={getFilteredFormalAssessments().length === 0}
          />
          {formData.activeFormalAssessments.length > 0 && (
            <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
              Selected assessments: {formData.activeFormalAssessments.length}
            </div>
          )}
        </div>

        {/* Tasks Selection */}
        <div className="form-group">
          <label className="label">
            Tasks from Selected Formal Assessment
          </label>
          {!selectedFormalAssessments || selectedFormalAssessments.length === 0 ? (
            <p style={{ color: '#666', fontStyle: 'italic', margin: '10px 0' }}>
              Please select a formal assessment first to see available tasks
            </p>
          ) : availableTasks.length === 0 ? (
            <p style={{ color: '#666', fontStyle: 'italic', margin: '10px 0' }}>
              No tasks available for the selected assessment
            </p>
          ) : (
            <div style={{ 
              border: '1px solid #ddd', 
              borderRadius: '4px', 
              padding: '10px',
              backgroundColor: '#f9f9f9',
              maxHeight: '300px',
              overflowY: 'auto'
            }}>
              {availableTasks.map((taskGroup, index) => {
                // Check if any instance of this task group is selected
                const isSelected = taskGroup.instances.some(instance => 
                  selectedTasks.some(selected => selected.originalIndex === instance.originalIndex)
                );
                
                return (
                  <div 
                    key={index}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      marginBottom: '8px',
                      padding: '8px',
                      backgroundColor: isSelected ? '#e8f5e8' : '#fff',
                      border: `1px solid ${isSelected ? '#28a745' : '#dee2e6'}`,
                      borderRadius: '4px'
                    }}
                  >
                    <input
                      type="checkbox"
                      id={`task-${index}`}
                      checked={isSelected}
                      onChange={() => handleTaskSelection(index)}
                      style={{ 
                        marginRight: '10px', 
                        marginTop: '2px',
                        width: '16px',
                        height: '16px',
                        cursor: 'pointer'
                      }}
                    />
                    <label 
                      htmlFor={`task-${index}`}
                      style={{ 
                        cursor: 'pointer',
                        fontSize: '14px',
                        lineHeight: '1.4',
                        color: '#333',
                        flex: 1
                      }}
                    >
                      <strong>
                        {taskGroup.taskName}
                        {taskGroup.instances.length > 1 && (
                          <span style={{ 
                            fontSize: '12px', 
                            color: '#666', 
                            marginLeft: '8px',
                            backgroundColor: '#f8f9fa',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontWeight: 'normal'
                          }}>
                            {taskGroup.instances.length} instances
                          </span>
                        )}
                      </strong>
                      {taskGroup.description && (
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                          {taskGroup.description.length > 100 
                            ? `${taskGroup.description.substring(0, 100)}...` 
                            : taskGroup.description
                          }
                        </div>
                      )}
                    </label>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected Tasks Display */}
        {selectedTasks.length > 0 && (
          <div className="form-group">
            <label className="label">Selected Tasks from Formal Assessment</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {selectedTasks.map((task, index) => (
                <div 
                  key={task.index} 
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '15px',
                    backgroundColor: '#f9f9f9'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                    <h4 style={{ margin: '0', color: '#333', fontSize: '16px' }}>
                      {task.task || `Task ${task.index + 1}`}
                      {task.instanceCount > 1 && (
                        <span style={{ 
                          fontSize: '12px', 
                          color: '#666', 
                          marginLeft: '8px',
                          backgroundColor: '#e9ecef',
                          padding: '2px 6px',
                          borderRadius: '4px'
                        }}>
                          Instance {selectedTasks.filter(t => t.groupName === task.groupName).findIndex(t => t.originalIndex === task.originalIndex) + 1} of {task.instanceCount}
                        </span>
                      )}
                    </h4>
                    <button
                      type="button"
                      onClick={() => removeTaskFromSelection(task.index)}
                      style={{
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '5px 10px',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      Remove Instance
                    </button>
                  </div>
                  
                  <div style={{ marginBottom: '10px' }}>
                    <strong>Description:</strong>
                    <p style={{ margin: '5px 0', color: '#666' }}>
                      {task.description || 'No description available'}
                    </p>
                  </div>

                  <div style={{ marginBottom: '10px' }}>
                    <strong>Hazards Identified:</strong>
                    {(() => {
                      // Use individual task hazards for each instance
                      const hazards = parseTaskHazards(task.hazards);
                      
                      if (hazards.length === 0) {
                        return (
                          <p style={{ margin: '5px 0', color: '#666', fontStyle: 'italic' }}>
                            No specific hazards identified for this task instance
                          </p>
                        );
                      }
                      
                      return (
                        <div style={{ marginTop: '8px' }}>
                          {hazards.map((hazard, hazardIndex) => (
                            <div 
                              key={hazardIndex}
                              style={{ 
                                display: 'flex', 
                                alignItems: 'flex-start', 
                                marginBottom: '6px',
                                padding: '6px 8px',
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                borderRadius: '3px'
                              }}
                            >
                              <span style={{ 
                                marginRight: '8px', 
                                marginTop: '0px',
                                fontSize: '14px',
                                color: '#666'
                              }}>
                                •
                              </span>
                              <span style={{ 
                                fontSize: '14px',
                                lineHeight: '1.3',
                                color: '#333',
                                flex: 1
                              }}>
                                {hazard}
                              </span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>

                  <div style={{ marginBottom: '10px' }}>
                    <strong>Risk Rating:</strong>
                    <span style={{ 
                      margin: '5px 0 5px 10px', 
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      backgroundColor: task.riskRating >= 15 ? '#dc3545' : 
                                      task.riskRating >= 12 ? '#fd7e14' : 
                                      task.riskRating >= 9 ? '#ffc107' : '#28a745',
                      color: 'white'
                    }}>
                      {task.riskRating || 'N/A'} - {
                        task.riskRating >= 15 ? 'Critical' :
                        task.riskRating >= 12 ? 'High' :
                        task.riskRating >= 9 ? 'Moderate' : 'Low'
                      }
                    </span>
                  </div>

                  <div>
                    <strong>Controls:</strong>
                    <p style={{ margin: '5px 0', color: '#666', whiteSpace: 'pre-wrap' }}>
                      {task.controls || 'No controls specified'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="label" htmlFor="jobTitle">
            Job Title
            {selectedFormalAssessments && selectedFormalAssessments.length > 0 && (
              <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                (Auto-populated from Formal Assessment)
              </span>
            )}
          </label>
          <input
            className="input"
            type="text"
            id="jobTitle"
            name="jobTitle"
            value={formData.jobTitle}
            onChange={handleInputChange}
            readOnly={selectedFormalAssessments && selectedFormalAssessments.length > 0}
            style={{
              backgroundColor: selectedFormalAssessments && selectedFormalAssessments.length > 0 ? '#f8f9fa' : 'white',
              cursor: selectedFormalAssessments && selectedFormalAssessments.length > 0 ? 'not-allowed' : 'text'
            }}
            placeholder={selectedFormalAssessments && selectedFormalAssessments.length > 0 ? 'Job positions from selected assessments will appear here' : 'Enter job title'}
          />
          {selectedFormalAssessments && selectedFormalAssessments.length > 0 && formData.jobTitle && (
            <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              Job positions from selected formal assessment: {formData.jobTitle}
            </small>
          )}
        </div>

        <div className="form-group">
          <PPEDropdown
            availablePPE={ppeOptions}
            selectedPPE={formData.ppeRequired}
            onChange={handlePPESelection}
          />
        </div>



        <div className="form-group">
          <EquipmentDropdown
            availableEquipment={getEquipment()}
            selectedEquipment={formData.equipmentUsed}
            onChange={handleEquipmentSelection}
          />
        </div>

        <div className="form-group">
          <label className="label" htmlFor="equipmentMaintenanceRepairRequired">
            Do any selected Equipment / Assets require maintenance or repair?
          </label>
          <select
            className="select"
            id="equipmentMaintenanceRepairRequired"
            name="equipmentMaintenanceRepairRequired"
            value={formData.equipmentMaintenanceRepairRequired}
            onChange={handleInputChange}
          >
            <option value="">Select...</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>

        {formData.equipmentMaintenanceRepairRequired === 'Yes' && (
          <div className="form-group">
            <label className="label" htmlFor="equipmentMaintenanceRepairNotes">
              Maintenance / Repair Details
            </label>
            <textarea
              className="textarea"
              id="equipmentMaintenanceRepairNotes"
              name="equipmentMaintenanceRepairNotes"
              value={formData.equipmentMaintenanceRepairNotes}
              onChange={handleInputChange}
              rows="4"
              placeholder="Enter details of the maintenance/repair required here"
            />
            <small className="conditional-field-hint">
              Enter details of the maintenance/repair required here.
            </small>
          </div>
        )}

        <div className="form-group">
          <AttendanceDropdown
            employees={employees}
            selected={attendance}
            onChange={setAttendance}
          />
        </div>

        <div className="form-group">
          <ContractorsDropdown
            contractors={availableContractors}
            selected={contractorsOnSite}
            onChange={setContractorsOnSite}
          />
        </div>

        <div className="form-group">
          <label className="label" htmlFor="permitRequired">
            Permit Required
          </label>
          <select
            className="select"
            id="permitRequired"
            name="permitRequired"
            value={formData.permitRequired}
            onChange={handleInputChange}
          >
            <option value="">Select...</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>

        {formData.permitRequired === 'Yes' && (
          <div className="form-group">
            <label className="label" htmlFor="permitNumber">
              Permit Number
            </label>
            <input
              className="input"
              type="text"
              id="permitNumber"
              name="permitNumber"
              value={formData.permitNumber}
              onChange={handleInputChange}
              placeholder="Enter permit number"
            />
          </div>
        )}

        <div className="form-group">
          <label className="label" htmlFor="diggingRequired">
            Digging
          </label>
          <select
            className="select"
            id="diggingRequired"
            name="diggingRequired"
            value={formData.diggingRequired}
            onChange={handleInputChange}
          >
            <option value="">Select...</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>

        {formData.diggingRequired === 'Yes' && (
          <div className="form-group">
            <label className="label" htmlFor="locateMapReference">
              Locate Map Reference Number
            </label>
            <input
              className="input"
              type="text"
              id="locateMapReference"
              name="locateMapReference"
              value={formData.locateMapReference}
              onChange={handleInputChange}
              placeholder="Enter locate map reference number"
            />
          </div>
        )}

        <div className="talking-points">
          <h3 className="talking-points-header">Talking Points</h3>
          <div className="form-group"></div>
          <textarea
            className="textarea"
            id="talkingPoints"
            name="talkingPoints"
            value={formData.talkingPoints || ''}
            onChange={handleInputChange}
            rows="4"
            placeholder="Enter any additional talking points here"
          />
        </div>

        <div className="form-group">
          <FormControlLabel
            control={(
              <Checkbox
                checked={sendAdvisorReminder}
                onChange={(event) => setSendAdvisorReminder(event.target.checked)}
                name="sendAdvisorReminder"
              />
            )}
            label="Notify Safety - Update Formal Hazard Assessment Template"
          />
        </div>

        {/* Attachments for hazard assessment */}
        <div className="form-group">
          <label className="label">Attachments (photos, documents)</label>
          <input
            type="file"
            id="hazard-attachments"
            multiple
            onChange={handleFileUpload}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt,.xlsx,.xls"
          />
          {attachments && attachments.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <h4 style={{ margin: '6px 0' }}>Selected Files:</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {attachments.map(att => (
                  <div key={att.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', border: '1px solid #e6e6e6', borderRadius: 6, background: '#fafafa' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{att.name}</div>
                      <div style={{ fontSize: 12, color: '#666' }}>{formatFileSize(att.size)} • {att.type || 'Unknown'}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {att.uploaded && <span style={{ color: 'green', fontSize: 12 }}>Uploaded</span>}
                      <button type="button" onClick={() => removeAttachment(att.id)} style={{ background: '#e53e3e', color: 'white', border: 'none', padding: '6px 10px', borderRadius: 4 }}>Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="label" htmlFor="supervisorName">
            Created By
          </label>
          <input
            className="input"
            type="text"
            id="supervisorName"
            name="supervisorName"
            value={formData.supervisorName}
            onChange={handleInputChange}
          />
        </div>

        <div className="date-time-container">
          <div>
            <label className="label" htmlFor="date">
              Date
            </label>
            <input
              className="input"
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
            />
          </div>
          <div>
            <label className="label" htmlFor="time">
              Time
            </label>
            <input
              className="input"
              type="time"
              id="time"
              name="time"
              value={formData.time}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <button
          type="submit"
          className="button button-active"
          title="Submit acknowledgment"
        >
          Submit Acknowledgment
        </button>
      </form>

      <Dialog
        open={openDialog}
        onClose={() => handleDialogClose(false)}
        disableEscapeKeyDown={true}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>Safety Guidelines</DialogTitle>
        <DialogContent>
          <p>Before proceeding with the job, ensure you have:</p>
          <FormControlLabel
            control={
              <Checkbox
                checked={safetyGuidelines.reviewedHazards}
                onChange={handleSafetyGuidelineChange}
                name="reviewedHazards"
              />
            }
            label="Reviewed all identified hazards with your team"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={safetyGuidelines.implementedControls}
                onChange={handleSafetyGuidelineChange}
                name="implementedControls"
              />
            }
            label="Implemented appropriate control measures"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={safetyGuidelines.properPPE}
                onChange={handleSafetyGuidelineChange}
                name="properPPE"
              />
            }
            label="Confirmed all workers have proper PPE and training"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={safetyGuidelines.emergencyProcedures}
                onChange={handleSafetyGuidelineChange}
                name="emergencyProcedures"
              />
            }
            label="Established emergency response procedures"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={safetyGuidelines.obtainedPermits}
                onChange={handleSafetyGuidelineChange}
                name="obtainedPermits"
              />
            }
            label="Obtained all necessary permits and authorizations"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleDialogClose(true)}
            color="primary"
            disabled={!allSafetyGuidelinesChecked}
          >
            Acknowledge
          </Button>
          <Button
            onClick={() => handleDialogClose(false)}
            color="secondary"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const EquipmentDropdown = ({ availableEquipment, selectedEquipment, onChange }) => {
  const equipmentArray = Array.isArray(selectedEquipment) ? selectedEquipment : [];

  return (
    <div className="equipment-dropdown-container">
      <CustomMultiSelect
        label="Equipment/Assets (Select all that apply)"
        options={availableEquipment.map((equipment) => ({ value: equipment, label: equipment }))}
        selectedValues={equipmentArray}
        onChange={(values) => onChange({ target: { value: values } })}
        placeholder="Select equipment/assets"
        emptyMessage="No equipment/assets available"
      />
    </div>
  );
};

const AttendanceDropdown = ({ employees, selected, onChange }) => (
  <CustomMultiSelect
    label="Employees in Attendance"
    options={employees.map((emp) => ({
      value: emp.email,
      label: formatEmployeeDisplayName(emp),
      secondary: emp.email
    }))}
    selectedValues={selected}
    onChange={onChange}
    placeholder="Select employees in attendance"
    emptyMessage="No employees available"
  />
);

const ContractorsDropdown = ({ contractors, selected, onChange }) => (
  <CustomMultiSelect
    label="Contractors on Site"
    options={contractors.map((contractor) => ({
      value: contractor.id,
      label: contractor.name,
      secondary: contractor.contact,
      searchText: [contractor.name, contractor.contact, contractor.email].filter(Boolean).join(' ')
    }))}
    selectedValues={selected}
    onChange={onChange}
    placeholder="Select contractors on site"
    emptyMessage="No contractors available"
    searchable
    searchPlaceholder="Search contractors"
    searchEmptyMessage="No contractors match your search"
  />
);

const PPEDropdown = ({ availablePPE, selectedPPE, onChange }) => {
  const safePPE = useMemo(
    () => (Array.isArray(selectedPPE) ? selectedPPE : []),
    [selectedPPE]
  );
  const [customPPE, setCustomPPE] = useState('');
  const [showCustomField, setShowCustomField] = useState(false);

  useEffect(() => {
    const existingCustomPPE = safePPE.find((ppe) => ppe.startsWith('Other:')) || '';
    setCustomPPE(existingCustomPPE.replace(/^Other:\s*/, ''));
    setShowCustomField(Boolean(existingCustomPPE));
  }, [safePPE]);

  const handlePPESelection = (value) => {
    const selectedValues = Array.isArray(value) ? value : [];

    const hasOther = selectedValues.includes('Other');
    setShowCustomField(hasOther);

    if (hasOther) {
      const normalizedValues = selectedValues.filter((selectedValue) => selectedValue !== 'Other');
      if (customPPE.trim()) {
        normalizedValues.push(`Other: ${customPPE.trim()}`);
      }
      onChange(normalizedValues);
    } else {
      onChange(selectedValues.filter((selectedValue) => selectedValue !== 'Other'));
    }
  };

  const handleCustomPPEChange = (event) => {
    const newCustomPPE = event.target.value;
    setCustomPPE(newCustomPPE);

    const updatedPPE = safePPE.filter(p => !p.startsWith('Other:'));
    if (newCustomPPE) {
      updatedPPE.push('Other: ' + newCustomPPE);
    }

    onChange(updatedPPE);
  };

  return (
    <div className="ppe-dropdown-container">
      <CustomMultiSelect
        label="PPE Required (Select all that apply)"
        options={[
          ...availablePPE.map((ppe) => ({ value: ppe, label: ppe })),
          { value: 'Other', label: 'Other' }
        ]}
        selectedValues={[
          ...safePPE.filter((ppe) => !ppe.startsWith('Other:')),
          ...(safePPE.some((ppe) => ppe.startsWith('Other:')) ? ['Other'] : [])
        ]}
        onChange={handlePPESelection}
        placeholder="Select required PPE"
        emptyMessage="No PPE options available"
      />

      {showCustomField && (
        <TextField
          fullWidth
          label="Other PPE Required"
          variant="outlined"
          value={customPPE}
          onChange={handleCustomPPEChange}
          className="custom-ppe-input"
          placeholder="Please describe the additional PPE required"
        />
      )}
    </div>
  );
};

export default HazardAssessmentAcknowledgment;